import React, { useState, useEffect } from 'react';
import { apiClient } from '@/utils/api';
import { useAuth } from '@/hooks/useAuth';
import toast from 'react-hot-toast';
import { ShoppingCart, Plus, Minus, X, Save, Package } from 'lucide-react';

interface McDonaldsOrderProps {
  slotId: string;
  onClose?: () => void;
}

const MENU_ITEMS = {
  burgers: [
    { name: 'Big Mac', emoji: '🍔', category: 'Burgers' },
    { name: 'Quarter Pounder', emoji: '🍔', category: 'Burgers' },
    { name: 'Double Cheeseburger', emoji: '🍔', category: 'Burgers' },
    { name: 'Cheeseburger', emoji: '🍔', category: 'Burgers' },
    { name: 'Hamburger', emoji: '🍔', category: 'Burgers' },
    { name: 'McDouble', emoji: '🍔', category: 'Burgers' },
  ],
  chicken: [
    { name: '20pc McNuggets', emoji: '🍗', category: 'Chicken' },
    { name: '10pc McNuggets', emoji: '🍗', category: 'Chicken' },
    { name: '6pc McNuggets', emoji: '🍗', category: 'Chicken' },
    { name: '4pc McNuggets', emoji: '🍗', category: 'Chicken' },
    { name: 'McChicken', emoji: '🍗', category: 'Chicken' },
    { name: 'Crispy Chicken Sandwich', emoji: '🍗', category: 'Chicken' },
    { name: 'Spicy McChicken', emoji: '🌶️', category: 'Chicken' },
  ],
  sides: [
    { name: 'Large Fries', emoji: '🍟', category: 'Sides' },
    { name: 'Medium Fries', emoji: '🍟', category: 'Sides' },
    { name: 'Small Fries', emoji: '🍟', category: 'Sides' },
    { name: 'Apple Slices', emoji: '🍎', category: 'Sides' },
    { name: 'Side Salad', emoji: '🥗', category: 'Sides' },
  ],
  drinks: [
    { name: 'Large Coke', emoji: '🥤', category: 'Drinks' },
    { name: 'Medium Coke', emoji: '🥤', category: 'Drinks' },
    { name: 'Small Coke', emoji: '🥤', category: 'Drinks' },
    { name: 'Sprite', emoji: '🥤', category: 'Drinks' },
    { name: 'Dr Pepper', emoji: '🥤', category: 'Drinks' },
    { name: 'Orange Juice', emoji: '🍊', category: 'Drinks' },
    { name: 'Apple Juice', emoji: '🍎', category: 'Drinks' },
    { name: 'Chocolate Milk', emoji: '🥛', category: 'Drinks' },
    { name: 'Sweet Tea', emoji: '🍵', category: 'Drinks' },
  ],
  desserts: [
    { name: 'McFlurry (Oreo)', emoji: '🍦', category: 'Desserts' },
    { name: 'McFlurry (M&M)', emoji: '🍦', category: 'Desserts' },
    { name: 'Apple Pie', emoji: '🥧', category: 'Desserts' },
    { name: 'Chocolate Chip Cookies', emoji: '🍪', category: 'Desserts' },
    { name: 'Vanilla Cone', emoji: '🍦', category: 'Desserts' },
    { name: 'Hot Fudge Sundae', emoji: '🍨', category: 'Desserts' },
  ],
  breakfast: [
    { name: 'Egg McMuffin', emoji: '🥚', category: 'Breakfast' },
    { name: 'Sausage McMuffin', emoji: '🌭', category: 'Breakfast' },
    { name: 'Hotcakes', emoji: '🥞', category: 'Breakfast' },
    { name: 'Hash Browns', emoji: '🥔', category: 'Breakfast' },
  ]
};

export default function McDonaldsOrder({ slotId, onClose }: McDonaldsOrderProps) {
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [specialRequests, setSpecialRequests] = useState('');
  const [existingOrders, setExistingOrders] = useState<any>({});
  const [loading, setLoading] = useState(false);
  const [activeCategory, setActiveCategory] = useState('burgers');
  const { user } = useAuth();

  useEffect(() => {
    loadExistingOrders();
  }, [slotId]);

  const loadExistingOrders = async () => {
    try {
      const response = await apiClient.get(`/api/meals/mcdonalds-orders/${slotId}`);
      setExistingOrders(response.orders || {});
      
      // Load user's existing order if any
      if (user && response.orders[user.id]) {
        const myOrder = response.orders[user.id];
        setSelectedItems(myOrder.items || []);
        setSpecialRequests(myOrder.special_requests || '');
      }
    } catch (error) {
      // Failed to load existing orders - continue with empty state
    }
  };

  const addItem = (itemName: string) => {
    setSelectedItems(prev => [...prev, itemName]);
  };

  const removeItem = (index: number) => {
    setSelectedItems(prev => prev.filter((_, i) => i !== index));
  };

  const saveOrder = async (closeAfterSave = true) => {
    if (selectedItems.length === 0) {
      toast.error('Please select at least one item');
      return;
    }

    setLoading(true);
    try {
      await apiClient.post(`/api/meals/mcdonalds-order/${slotId}`, {
        items: selectedItems,
        special_requests: specialRequests || null
      });
      
      toast.success('Your McDonald\'s order has been saved!');
      await loadExistingOrders();
      
      // Clear the current order to allow adding more
      setSelectedItems([]);
      setSpecialRequests('');
      
      if (closeAfterSave && onClose) {
        onClose();
      }
    } catch (error: any) {
      // Error saving order - handled by toast notification
      toast.error(error.response?.data?.detail || 'Failed to save order');
    } finally {
      setLoading(false);
    }
  };

  const categories = Object.keys(MENU_ITEMS);
  const currentItems = MENU_ITEMS[activeCategory as keyof typeof MENU_ITEMS];

  return (
    <div className="bg-white rounded-lg shadow-lg p-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <Package className="w-8 h-8 text-red-600" />
          <h2 className="text-2xl font-bold text-gray-900">McDonald's Order</h2>
        </div>
        {onClose && (
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700">
            <X className="w-6 h-6" />
          </button>
        )}
      </div>

      {/* Category Tabs */}
      <div className="flex space-x-2 mb-6 overflow-x-auto pb-2">
        {categories.map(category => (
          <button
            key={category}
            onClick={() => setActiveCategory(category)}
            className={`px-4 py-2 rounded-lg font-medium whitespace-nowrap transition-colors ${
              activeCategory === category
                ? 'bg-red-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            {category.charAt(0).toUpperCase() + category.slice(1)}
          </button>
        ))}
      </div>

      {/* Menu Items Grid */}
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 mb-6">
        {currentItems.map((item, index) => (
          <button
            key={index}
            onClick={() => addItem(item.name)}
            className="bg-yellow-50 hover:bg-yellow-100 border-2 border-yellow-300 rounded-lg p-3 text-center transition-colors"
          >
            <div className="text-2xl mb-1">{item.emoji}</div>
            <div className="text-sm font-medium text-gray-800">{item.name}</div>
          </button>
        ))}
      </div>

      {/* Selected Items */}
      <div className="bg-gray-50 rounded-lg p-4 mb-6">
        <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
          <ShoppingCart className="w-5 h-5 mr-2" />
          Your Order ({selectedItems.length} items)
        </h3>
        
        {selectedItems.length === 0 ? (
          <p className="text-gray-500 text-sm">No items selected yet</p>
        ) : (
          <div className="space-y-2">
            {selectedItems.map((item, index) => (
              <div key={index} className="flex items-center justify-between bg-white rounded-lg px-3 py-2">
                <span className="text-sm font-medium">{item}</span>
                <button
                  onClick={() => removeItem(index)}
                  className="text-red-600 hover:text-red-800"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Special Requests */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Special Requests (Optional)
        </label>
        <textarea
          value={specialRequests}
          onChange={(e) => setSpecialRequests(e.target.value)}
          placeholder="No pickles, extra ketchup, etc..."
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
          rows={3}
        />
      </div>

      {/* Other Orders */}
      {Object.keys(existingOrders).length > 0 && (
        <div className="bg-blue-50 rounded-lg p-4 mb-6">
          <h3 className="font-semibold text-gray-900 mb-3">Family Orders</h3>
          <div className="space-y-2">
            {Object.entries(existingOrders).map(([userId, order]: [string, any]) => {
              // Format items properly whether they're an array or already a string
              const itemsDisplay = Array.isArray(order.items) 
                ? order.items.join(', ') 
                : (typeof order.items === 'string' ? order.items : JSON.stringify(order.items));
              
              return (
                <div key={userId} className="text-sm">
                  <span className="font-medium">{order.user_name}:</span>
                  <span className="ml-2 text-gray-600">
                    {itemsDisplay}
                    {order.special_requests && (
                      <span className="italic ml-1">({order.special_requests})</span>
                    )}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Save Buttons */}
      <div className="flex space-x-3">
        <button
          onClick={() => saveOrder(false)}
          disabled={loading || selectedItems.length === 0}
          className={`flex-1 py-3 rounded-lg font-medium flex items-center justify-center space-x-2 transition-colors ${
            loading || selectedItems.length === 0
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-yellow-500 text-white hover:bg-yellow-600'
          }`}
        >
          <Plus className="w-5 h-5" />
          <span>{loading ? 'Saving...' : 'Save & Add More'}</span>
        </button>
        <button
          onClick={() => saveOrder(true)}
          disabled={loading || selectedItems.length === 0}
          className={`flex-1 py-3 rounded-lg font-medium flex items-center justify-center space-x-2 transition-colors ${
            loading || selectedItems.length === 0
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-red-600 text-white hover:bg-red-700'
          }`}
        >
          <Save className="w-5 h-5" />
          <span>{loading ? 'Saving...' : 'Save & Close'}</span>
        </button>
      </div>
    </div>
  );
}